import * as signalR from '@microsoft/signalr';
import {queryClient, WSSBase} from 'App/App';
import {AxiosError} from 'axios';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {WebSocketError} from 'types/common/webSocketError';
import {ApiError} from 'types/dtos/ApiError';
import {MediaChangeDAO, ScreenDTO, StyleChangeDAO} from 'types/dtos/Screen';
import {GlobalNotificationWSHandler} from 'utils/errorHandler';

const ConnectionKey = 'connection';
const ScreenKey = 'screen';
const AllScreenKey = 'screens';
const ScreenUpdaterKey = 'screenUpdater';

const ManagerHub = {
  Screen: {
    OnScreenAdded: 'ScreenAdded',
    OnScreenRemoved: 'ScreenRemoved',

    GetAllScreens: 'GetAllScreens',
    OnGetAllScreens: 'SetAllScreens',
  },
  Media: {
    SourceToScreen: 'SourceToScreen',
    StyleToScreen: 'StyleToScreen',
    OnSourceSet: 'ScreenSetOk',
  },
};

export function useConnectWS<TData = signalR.HubConnection>(
  wssAddr: string,
  errorHandler?: (error: WebSocketError) => void,
  options?: UseQueryOptions<TData, AxiosError, TData>,
): UseQueryResult<TData, AxiosError<any>> {
  return useQuery(
    ConnectionKey,
    async (): Promise<TData> => {
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${WSSBase}${wssAddr}`, {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .build();

      await connection.start();

      if (connection.state !== signalR.HubConnectionState.Connected)
        throw new WebSocketError('Не удалось подключиться к серверу');

      connection.on(ManagerHub.Screen.OnScreenAdded, (screen: ScreenDTO) => {
        queryClient.setQueryData([ScreenKey, screen.ScreenId], screen);
        queryClient.invalidateQueries(AllScreenKey);
      });

      connection.on(ManagerHub.Screen.OnScreenRemoved, (screenId: string) => {
        queryClient.removeQueries([ScreenKey, screenId]);
        queryClient.invalidateQueries(AllScreenKey);
      });

      await connection.send('ConnectAdminPanel');

      return connection as unknown as TData;
    },
    {
      onError: errorHandler || GlobalNotificationWSHandler,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
}

export function useScreens(
  errorHandler?: (error: WebSocketError) => void,
  options?: UseQueryOptions<ScreenDTO[], AxiosError, ScreenDTO[]>,
): UseQueryResult<ScreenDTO[], AxiosError<any>> {
  return useQuery(
    AllScreenKey,
    async (): Promise<ScreenDTO[]> => {
      const connection =
        queryClient.getQueryData<signalR.HubConnection>(ConnectionKey);

      return new Promise<ScreenDTO[]>((res, rej) => {
        if (!connection) {
          rej(new WebSocketError('No connection established'));
          return;
        }

        connection.on(
          ManagerHub.Screen.OnGetAllScreens,
          (screens: {screens: ScreenDTO[]}): void => {
            res(screens.screens);
          },
        );

        connection.send(ManagerHub.Screen.GetAllScreens);
      });
    },
    {
      onSuccess: (data) => {
        data.forEach((element) =>
          queryClient.setQueryData<ScreenDTO>(
            [ScreenKey, {id: element.ScreenId}],
            element,
          ),
        );
      },
      onError: errorHandler || GlobalNotificationWSHandler,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
}

export const useSetMedia = (
  errorHandler?: (error: AxiosError<any>) => void,
) => {
  return useMutation<ScreenDTO, AxiosError<ApiError>, MediaChangeDAO>(
    ScreenUpdaterKey,
    async (data: MediaChangeDAO): Promise<ScreenDTO> => {
      const connection =
        queryClient.getQueryData<signalR.HubConnection>(ConnectionKey);

      return new Promise<ScreenDTO>((res, rej) => {
        if (!connection) {
          rej(new WebSocketError('No connection established'));
          return;
        }

        connection.on(
          ManagerHub.Media.OnSourceSet,
          (screenData: ScreenDTO): void => {
            res(screenData);
          },
        );

        connection.send(ManagerHub.Media.SourceToScreen, data);
      });
    },
    {
      onSuccess: (data, request) => {
        queryClient.setQueryData<ScreenDTO>(
          [ScreenKey, request.screenId],
          data,
        );
      },
      onError: errorHandler || GlobalNotificationWSHandler,
    },
  );
};

export const useSetStyle = (
  errorHandler?: (error: AxiosError<any>) => void,
) => {
  return useMutation<ScreenDTO, AxiosError<ApiError>, StyleChangeDAO>(
    ScreenUpdaterKey,
    async (data: StyleChangeDAO): Promise<ScreenDTO> => {
      const connection =
        queryClient.getQueryData<signalR.HubConnection>(ConnectionKey);

      return new Promise<ScreenDTO>((res, rej) => {
        if (!connection) {
          rej(new WebSocketError('No connection established'));
          return;
        }

        connection.on(
          ManagerHub.Media.OnSourceSet,
          (screenData: ScreenDTO): void => {
            res(screenData);
          },
        );

        connection.send(ManagerHub.Media.StyleToScreen, data);
      });
    },
    {
      onSuccess: (data, request) => {
        queryClient.setQueryData<ScreenDTO>(
          [ScreenKey, request.screenId],
          data,
        );
      },
      onError: errorHandler || GlobalNotificationWSHandler,
    },
  );
};
